import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Popup/Modal';
import Toast from './Popup/Toast';
import { useSubmitFeedback } from '../hooks/useQueries';

export default function FeedbackModal({ show, onClose }) {
  const [feedbackText, setFeedbackText] = useState('');
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Get mutation hook
  const submitFeedbackMutation = useSubmitFeedback();

  const screenshotPreviewUrl = useMemo(() => {
    if (!screenshotFile) return null;
    return URL.createObjectURL(screenshotFile);
  }, [screenshotFile]);

  useEffect(() => {
    return () => {
      if (screenshotPreviewUrl) URL.revokeObjectURL(screenshotPreviewUrl);
    };
  }, [screenshotPreviewUrl]);

  const validateAndSetScreenshot = (file) => {
    if (!file) return;

    if (!file.type || !file.type.startsWith('image/')) {
      setToast({
        show: true,
        message: 'Please select an image file (PNG, JPG, etc.).',
        type: 'error'
      });
      return;
    }

    // Client-side limit; server enforces too
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      setToast({
        show: true,
        message: 'Image is too large. Please keep it under 5 MB.',
        type: 'error'
      });
      return;
    }

    setScreenshotFile(file);
  };

  const handleSubmit = async () => {
    // Validate feedback text
    if (!feedbackText.trim()) {
      setToast({
        show: true,
        message: 'Please enter your feedback before submitting.',
        type: 'error'
      });
      return;
    }

    try {
      // Capture current context
      const currentUrl = window.location.href;
      
      // Capture localStorage data
      const localStorageData = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        localStorageData[key] = localStorage.getItem(key);
      }
      
      // Capture sessionStorage data
      const sessionStorageData = {};
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        sessionStorageData[key] = sessionStorage.getItem(key);
      }
      
      // Capture browser info
      const browserInfo = navigator.userAgent;

      // Create payload (multipart if screenshot is provided)
      const sessionData = {
        localStorage: localStorageData,
        sessionStorage: sessionStorageData
      };

      let payload;
      if (screenshotFile) {
        const formData = new FormData();
        formData.append('feedback_text', feedbackText.trim());
        formData.append('current_url', currentUrl);
        formData.append('session_data', JSON.stringify(sessionData));
        formData.append('browser_info', browserInfo);
        formData.append('screenshot', screenshotFile, screenshotFile.name);
        payload = formData;
      } else {
        payload = {
          feedback_text: feedbackText.trim(),
          current_url: currentUrl,
          session_data: sessionData,
          browser_info: browserInfo
        };
      }

      // Submit feedback
      await submitFeedbackMutation.mutateAsync(payload);
      
      // Show success and close modal
      setToast({
        show: true,
        message: 'Feedback submitted successfully!',
        type: 'success'
      });
      
      setTimeout(() => {
        handleClose();
      }, 1000);
      
    } catch (error) {
      setToast({
        show: true,
        message: 'Failed to submit feedback. Please try again.',
        type: 'error'
      });
    }
  };

  const handleClose = () => {
    setFeedbackText('');
    setScreenshotFile(null);
    onClose();
  };

  const textareaStyles = {
    width: '100%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '1.4',
    resize: 'vertical',
    fontFamily: 'inherit'
  };

  const content = (
    <div>
      <p style={{ marginBottom: '16px', color: '#666' }}>
        Please describe your feedback, issue, or suggestion. We'll review it and get back to you.
      </p>
      <textarea
        value={feedbackText}
        onChange={(e) => setFeedbackText(e.target.value)}
        placeholder="Enter your feedback here..."
        rows={6}
        style={textareaStyles}
      />

      <div style={{ marginTop: '16px' }}>
        <div style={{ fontSize: '13px', color: '#444', marginBottom: '8px', fontWeight: 600 }}>
          Screenshot (optional)
        </div>

        <label
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const file = e.dataTransfer?.files?.[0];
            validateAndSetScreenshot(file);
          }}
          style={{
            display: 'block',
            border: '2px dashed #cbd5e1',
            borderRadius: '8px',
            padding: '14px',
            background: '#f8fafc',
            cursor: 'pointer'
          }}
        >
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => validateAndSetScreenshot(e.target.files?.[0])}
          />
          {!screenshotFile ? (
            <div style={{ color: '#475569', fontSize: '13px' }}>
              Drag and drop an image here, or click to choose a file.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {screenshotPreviewUrl ? (
                <img
                  src={screenshotPreviewUrl}
                  alt="Screenshot preview"
                  style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              ) : null}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {screenshotFile.name}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {(screenshotFile.size / 1024).toFixed(0)} KB
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setScreenshotFile(null);
                }}
                style={{
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  borderRadius: '6px',
                  padding: '8px 10px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Remove
              </button>
            </div>
          )}
        </label>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        show={show}
        onCancel={handleClose}
        onConfirm={handleSubmit}
        title="Leave Feedback or Report Issue"
        confirmText="Submit Feedback"
        cancelText="Cancel"
        confirmDisabled={!feedbackText.trim()}
        loading={submitFeedbackMutation.isPending}
        content={content}
      />
      
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: '', type: 'success' })}
      />
    </>
  );
}